import {
  Button,
  Caption1,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  mergeClasses,
  Option,
  Skeleton,
  SkeletonItem,
  Spinner,
  Switch,
  Text,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
  DeleteRegular,
  DismissRegular,
} from "@fluentui/react-icons";
import {
  addProjectIqWorkspaceDocument,
  createProjectIqWorkspace,
  deleteWorkspaceLogo,
  getProjectIqCapabilities,
  getProjectIqWorkspaces,
  registerWorkspaceLogo,
  updateProjectIqWorkspace,
  addCustomCapability,
  updateCustomCapability,
  deleteCustomCapability,
  deleteProjectIqWorkspaceDocument,
} from "apps/project-iq/api";
import { usePortfolios } from "apps/project-iq/hooks/usePortfolios";
import { usePractices } from "apps/project-iq/hooks/usePractices";
import { ICapability } from "apps/project-iq/models/ICapability";
import { ICustomCapability } from "apps/project-iq/models/ICustomCapability";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { Constants, ConstantValues } from "common/constants";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { FileDropzone } from "components/fileDropzone/FileDropzone";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { IAzureUser } from "models/IAzureUser";
import {
  ICreateOrUpdateProjectIQWorkspace,
  IProjectIQWorkspace,
} from "models/IProjectIQWorkspace";
import { IUser } from "models/IUser";
import { IWorkspaceLibraryDocument } from "components/manageWorkspaces/workspaceLibrary/workspaceLibrary.types";
import { IUserDetails } from "models/IUserDetails";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addProjectIqWorkspace,
  selectAvailableProjectIqWorkspaces,
  setProjectIqWorkspaces,
  updateProjectIqWorkspace as updateProjectIqWorkspaceAction,
} from "redux/projectIqWorkspacesSlice";
import { RootState } from "store";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedDropdownStyles,
} from "theme/sharedStyles";
import {
  safeDecodeURIComponent,
  uploadFilesToStorage,
  uploadLogoToStorage,
} from "utils/blobUtils";
import { getFieldValidation } from "utils/dialogUtils";
import { mapAzureUsersToModel } from "utils/generalUtils";
import {
  deduplicateOptions,
  optionToUserDetails,
  userDetailsToOption,
  validateDuplicateName,
  validateOwners,
  validateRequiredFields,
  validateSelfRemoval,
  WORKSPACE_ERROR_MESSAGES,
  WORKSPACE_FIELD_LIMITS,
  WORKSPACE_PICKER_IDS,
} from "utils/workspaceFormValidation";
import { ContextTab } from "./ContextTab/ContextTab";
import {
  getDefaultTeamName,
  MicrosoftTeamsTab,
} from "./MicrosoftTeamsTab/MicrosoftTeamsTab";
import { IMicrosoftTeam } from "./MicrosoftTeamsTab/ExistingTeamPicker/ExistingTeamPicker.types";
import { TeamSource } from "./MicrosoftTeamsTab/MicrosoftTeamsTab.types";
import { useStyles } from "./EditProjectIqWorkspaceDialog.styles";
import {
  DIALOG_LABELS,
  HEX_COLOR_PATTERN,
  HEX_COLOR_VALIDATION_MESSAGE,
  IEditProjectIqWorkspaceDialogProps,
  ISyncWorkspaceLogoOptions,
  ISyncWorkspaceLogoResult,
  WorkspaceTab,
} from "./EditProjectIqWorkspaceDialog.types";
import { CustomCapabilityCard } from "./CustomCapabilityCard";
import { CustomCapabilityAddCard } from "./CustomCapabilityAddCard";
import { WorkspaceBrandingPreview } from "./WorkspaceBrandingPreview";

const CLIENT_NAME_MAX_LENGTH = 150;
const CLIENT_NAME_WARNING_THRESHOLD = 120;
const PROJECT_IQ_WORKSPACE_LOGO_CONTAINER = "project-iq-workspace-logo";

export const syncWorkspaceLogo = async (
  workspaceId: string,
  {
    logoFile,
    headerLogoUrl,
    originalLogoUrl,
    customBrandingEnabled,
  }: ISyncWorkspaceLogoOptions,
): Promise<ISyncWorkspaceLogoResult> => {
  if (!customBrandingEnabled) {
    return {};
  }

  const hasExistingLogoBlob = Boolean(originalLogoUrl?.startsWith("http"));

  if (logoFile) {
    const blobName = await uploadLogoToStorage(
      logoFile,
      PROJECT_IQ_WORKSPACE_LOGO_CONTAINER,
      workspaceId,
    );
    const logoResult = await registerWorkspaceLogo(workspaceId, blobName);

    if (hasExistingLogoBlob) {
      try {
        await deleteWorkspaceLogo(workspaceId, originalLogoUrl);
      } catch (deleteError) {
        console.warn("Failed to delete old workspace logo blob:", deleteError);
      }
    }

    return { headerLogoUrl: logoResult.url };
  }

  if (!headerLogoUrl && hasExistingLogoBlob) {
    await deleteWorkspaceLogo(workspaceId, originalLogoUrl);
    return { headerLogoUrl: null };
  }

  return { headerLogoUrl: headerLogoUrl || undefined };
};

export const buildBrandingColorPayloadFields = (
  customBrandingEnabled: boolean,
  headerColor: string,
  accentColor: string,
): { headerColor: string; accentColor: string } | Record<string, never> =>
  customBrandingEnabled ? { headerColor, accentColor } : {};

export const EditProjectIqWorkspaceDialog = ({
  isOpen,
  workspace,
  onDismiss,
  onSave,
  initialTab,
}: IEditProjectIqWorkspaceDialogProps): JSX.Element => {
  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );
  const allProjectIqWorkspaces = useSelector(
    selectAvailableProjectIqWorkspaces,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const { portfolios, isLoading: isPortfoliosLoading } = usePortfolios();
  const { practices, isLoading: isPracticesLoading } = usePractices();
  const sortedPortfolios = useMemo(
    () => [...portfolios].sort((a, b) => a.label.localeCompare(b.label)),
    [portfolios],
  );
  const sortedPractices = useMemo(
    () => [...practices].sort((a, b) => a.label.localeCompare(b.label)),
    [practices],
  );

  const dispatch = useDispatch();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();
  const dropdownStyles = useSharedDropdownStyles();
  const styles = useStyles();

  const isEdit = workspace !== null;
  const isPlatformAdmin = currentUser.roles.includes(
    Constants.PLATFORM_ADMINS_GROUP_NAME_VALUE,
  );
  const isOwner = workspace?.owners?.some(
    (owner) => owner.userId === currentUser.id,
  );
  const canEditOwners = isPlatformAdmin || isOwner;

  const [activeTab, setActiveTab] = useState<WorkspaceTab>(
    initialTab ?? "details",
  );
  const [name, setName] = useState<string>(ConstantValues.EMPTY_STRING);
  const [clientName, setClientName] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [teamMembers, setTeamMembers] = useState<CustomOption[]>([]);
  const [owners, setOwners] = useState<CustomOption[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [code, setCode] = useState<string>(workspace?.code ?? "");
  const [portfolioId, setPortfolioId] = useState<string>(
    workspace?.portfolioId ?? "",
  );
  const [practiceIds, setPracticeIds] = useState<string[]>(
    workspace?.practiceIds ?? [],
  );
  const [description, setDescription] = useState<string>(
    workspace?.description ?? "",
  );
  const [headerColor, setHeaderColor] = useState<string>(
    workspace?.headerColor || projectIqTokens.navy,
  );
  const [accentColor, setAccentColor] = useState<string>(
    workspace?.accentColor || projectIqTokens.teal,
  );
  const [headerLogoUrl, setHeaderLogoUrl] = useState<string>(
    workspace?.headerLogoUrl || ConstantValues.EMPTY_STRING,
  );
  const [customBrandingEnabled, setCustomBrandingEnabled] = useState<boolean>(
    workspace?.customBrandingEnabled ?? false,
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [originalLogoUrl, setOriginalLogoUrl] = useState<string | undefined>(
    workspace?.headerLogoUrl ?? undefined,
  );
  const [capabilities, setCapabilities] = useState<string[]>(
    workspace?.capabilities?.map((c) => c.slug) ?? [],
  );
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [connectedSources, setConnectedSources] = useState<string[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<
    IWorkspaceLibraryDocument[]
  >([]);
  const [teamSource, setTeamSource] = useState<TeamSource>("existing");
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [editedTeamName, setEditedTeamName] = useState<string | null>(null);
  const [availableCapabilities, setAvailableCapabilities] = useState<
    ICapability[]
  >([]);
  const [isCapabilitiesLoadError, setIsCapabilitiesLoadError] = useState(false);
  const [isCapabilitiesLoading, setIsCapabilitiesLoading] = useState(true);
  const [localCustomCaps, setLocalCustomCaps] = useState<ICustomCapability[]>(
    [],
  );
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    url: "",
    description: "",
    isEnabled: true,
  });
  const [addFormErrors, setAddFormErrors] = useState<{
    name?: string;
    url?: string;
  }>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null,
  );
  const [editForm, setEditForm] = useState({
    name: "",
    url: "",
    description: "",
    isEnabled: true,
  });
  const [editFormErrors, setEditFormErrors] = useState<{
    name?: string;
    url?: string;
  }>({});
  const [originalCustomCaps, setOriginalCustomCaps] = useState<
    ICustomCapability[]
  >([]);
  const [draftIds, setDraftIds] = useState<Set<string>>(new Set());

  const tabOrder = useMemo<WorkspaceTab[]>(() => {
    const tabs: WorkspaceTab[] = ["details", "settings", "microsoftTeams"];
    //   TODO: Uncomment when the context & files tab is restored.
    // const tabs: WorkspaceTab[] = ["details", "settings", "context", "microsoftTeams"];
    {/*   TODO: Uncomment when the client view and the Custom branding tab are restored.*/ }
    // if (customBrandingEnabled) tabs.push("branding");
    return tabs;
  }, [customBrandingEnabled]);
  const lastTab = tabOrder[tabOrder.length - 1];

  useEffect((): void => {
    setIsCapabilitiesLoading(true);
    getProjectIqCapabilities()
      .then((data: ICapability[]) => {
        setAvailableCapabilities(data);
        setIsCapabilitiesLoading(false);
      })
      .catch((_e: unknown) => {
        setIsCapabilitiesLoadError(true);
        setIsCapabilitiesLoading(false);
      });
  }, []);

  useEffect((): void => {
    setStagedFiles([]);
    setConnectedSources([]);
    setTeamSource("existing");
    setSelectedTeamId(null);
    setEditedTeamName(null);
    setActiveTab(initialTab ?? "details");
    if (workspace) {
      setName(workspace.name);
      setClientName(workspace.clientName);
      setTeamMembers((workspace.teamMembers || []).map(userDetailsToOption));
      setOwners((workspace.owners || []).map(userDetailsToOption));
      setCode(workspace.code ?? "");
      setPortfolioId(workspace.portfolioId ?? "");
      setPracticeIds(workspace.practiceIds ?? []);
      setDescription(workspace.description ?? "");
      setHeaderColor(workspace.headerColor || projectIqTokens.navy);
      setAccentColor(workspace.accentColor || projectIqTokens.teal);
      setHeaderLogoUrl(workspace.headerLogoUrl || ConstantValues.EMPTY_STRING);
      setCustomBrandingEnabled(workspace.customBrandingEnabled ?? false);
      setLogoFile(null);
      setOriginalLogoUrl(workspace.headerLogoUrl ?? undefined);
      setCapabilities(workspace.capabilities?.map((c) => c.slug) ?? []);
      setLocalCustomCaps(workspace.customCapabilities ?? []);
      setOriginalCustomCaps(workspace.customCapabilities ?? []);
      setDraftIds(new Set());
      setExistingDocuments(
        (workspace.libraryDocuments ?? []).filter((d) => d.source === "file"),
      );
    } else {
      setName("");
      setClientName("");
      setTeamMembers([]);
      setCode("");
      setPortfolioId("");
      setPracticeIds([]);
      setDescription("");
      setHeaderColor(projectIqTokens.navy);
      setAccentColor(projectIqTokens.teal);
      setHeaderLogoUrl(ConstantValues.EMPTY_STRING);
      setCustomBrandingEnabled(false);
      setLogoFile(null);
      setOriginalLogoUrl(undefined);
      setCapabilities([]);
      setFieldErrors({});
      setOwners([
        {
          id: currentUser.id,
          key: currentUser.id,
          text: currentUser.displayName,
        },
      ]);
      setLocalCustomCaps([]);
      setOriginalCustomCaps([]);
      setDraftIds(new Set());
      setShowAddForm(false);
      setAddForm({ name: "", url: "", description: "", isEnabled: true });
      setAddFormErrors({});
      setEditingId(null);
      setConfirmingDeleteId(null);
      setExistingDocuments([]);
    }
  }, [workspace, currentUser, isOpen, initialTab]);

  const toggleCapability = (slug: string): void =>
    setCapabilities((prev) =>
      prev.includes(slug) ? prev.filter((c) => c !== slug) : [...prev, slug],
    );

  const toggleConnectedSource = (slug: string): void =>
    setConnectedSources((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );

  const handleStagedFilesSelected = (files: File[]): void => {
    setStagedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const newFiles = files.filter((f) => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });
  };

  const removeStagedFile = (fileName: string): void =>
    setStagedFiles((prev) => prev.filter((f) => f.name !== fileName));

  const handleRemoveExistingDocument = async (
    filename: string,
  ): Promise<void> => {
    if (!workspace?.id) return;
    setExistingDocuments((prev) => prev.filter((d) => d.filename !== filename));
    await deleteProjectIqWorkspaceDocument({
      workspaceId: workspace.id,
      filename,
    });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value);
    if (fieldErrors.name) {
      setFieldErrors((prev) => ({
        ...prev,
        name: ConstantValues.EMPTY_STRING,
      }));
    }
  };

  const handleClientNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setClientName(e.target.value);
    if (fieldErrors.clientName) {
      setFieldErrors((prev) => ({
        ...prev,
        clientName: ConstantValues.EMPTY_STRING,
      }));
    }
  };

  const handleOwnersChange = (selected: CustomOption[]): void => {
    setOwners(deduplicateOptions(selected));
    if (fieldErrors.owners) {
      setFieldErrors((prev) => ({
        ...prev,
        owners: ConstantValues.EMPTY_STRING,
      }));
    }
  };

  const handleSelectedTeamMembers = (selected: CustomOption[]): void => {
    setTeamMembers(deduplicateOptions(selected));
    if (fieldErrors.teamMembers) {
      setFieldErrors((prev) => ({
        ...prev,
        teamMembers: ConstantValues.EMPTY_STRING,
      }));
    }
  };

  const handleCustomBrandingToggle = (checked: boolean): void => {
    setCustomBrandingEnabled(checked);
    if (!checked && activeTab === "branding") {
      setActiveTab("context");
    }
  };

  const handleLogoFileSelect = (files: File[]): void => {
    if (files.length > 0) {
      const file = files[0];
      setLogoFile(file);
      setFieldErrors((prev) => {
        const { logo: _logo, ...rest } = prev;
        return rest;
      });
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setHeaderLogoUrl(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoFileRemove = (): void => {
    setLogoFile(null);
    setHeaderLogoUrl(ConstantValues.EMPTY_STRING);
  };

  const handleHeaderColorChange = (value: string): void => {
    setHeaderColor(value);
    setFieldErrors((prev) => {
      if (!prev.headerColor) return prev;
      const { headerColor: _headerColor, ...rest } = prev;
      return rest;
    });
  };

  const handleHeaderColorBlur = (): void => {
    if (!HEX_COLOR_PATTERN.test(headerColor)) {
      setFieldErrors((prev) => ({
        ...prev,
        headerColor: HEX_COLOR_VALIDATION_MESSAGE,
      }));
    }
  };

  const handleAccentColorChange = (value: string): void => {
    setAccentColor(value);
    setFieldErrors((prev) => {
      if (!prev.accentColor) return prev;
      const { accentColor: _accentColor, ...rest } = prev;
      return rest;
    });
  };

  const handleAccentColorBlur = (): void => {
    if (!HEX_COLOR_PATTERN.test(accentColor)) {
      setFieldErrors((prev) => ({
        ...prev,
        accentColor: HEX_COLOR_VALIDATION_MESSAGE,
      }));
    }
  };

  const readResourcesForPickerFieldFiltered = useCallback(
    async (searchedValue: string): Promise<IAzureUser[]> => {
      const mappedUsers = mapAzureUsersToModel(users);
      return mappedUsers.filter((resource: IAzureUser) =>
        resource.Name?.toLowerCase().includes(searchedValue.toLowerCase()),
      );
    },
    [users],
  );

  const validateCapForm = (values: {
    name: string;
    url: string;
  }): { name?: string; url?: string } => {
    const errors: { name?: string; url?: string } = {};
    if (!values.name.trim()) errors.name = "Name is required";
    if (!values.url.trim()) {
      errors.url = "URL is required";
    } else {
      try {
        const parsed = new URL(values.url.trim());
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          errors.url = "URL must use http or https";
        }
      } catch {
        errors.url = "Please enter a valid URL (e.g. https://example.com)";
      }
    }
    return errors;
  };

  const validateDetailsFields = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (name.length > WORKSPACE_FIELD_LIMITS.NAME_MAX_LENGTH) {
      errors.name = `Workspace Name must be ${WORKSPACE_FIELD_LIMITS.NAME_MAX_LENGTH} characters or fewer.`;
    }
    if (clientName.length > CLIENT_NAME_MAX_LENGTH) {
      errors.clientName = `Client Name must be ${CLIENT_NAME_MAX_LENGTH} characters or fewer.`;
    }

    const requiredErrors = validateRequiredFields(name, clientName);
    if (requiredErrors.clientName) {
      requiredErrors.clientName = "Client Name is required";
    }
    Object.assign(errors, requiredErrors);

    const nameChanged =
      !workspace ||
      name.trim().toLowerCase() !== workspace.name.trim().toLowerCase();

    if (nameChanged) {
      Object.assign(
        errors,
        validateDuplicateName(name, allProjectIqWorkspaces, workspace?.id),
      );
    }

    if (code.trim()) {
      const codeConflict = allProjectIqWorkspaces.find(
        (w) =>
          w.id !== workspace?.id &&
          w.code?.trim().toLowerCase() === code.trim().toLowerCase(),
      );
      if (codeConflict) {
        errors.code = "A workspace with this code already exists";
      }
    }

    return errors;
  };

  const validateBrandingFields = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!customBrandingEnabled) {
      return errors;
    }

    if (!HEX_COLOR_PATTERN.test(headerColor)) {
      errors.headerColor = HEX_COLOR_VALIDATION_MESSAGE;
    }
    if (!HEX_COLOR_PATTERN.test(accentColor)) {
      errors.accentColor = HEX_COLOR_VALIDATION_MESSAGE;
    }

    return errors;
  };

  const handleAddCustomCap = (): void => {
    const errors = validateCapForm(addForm);
    if (Object.keys(errors).length) {
      setAddFormErrors(errors);
      return;
    }
    const newId = crypto.randomUUID();
    setLocalCustomCaps((prev) => [
      ...prev,
      {
        id: newId,
        name: addForm.name.trim(),
        url: addForm.url.trim(),
        description: addForm.description.trim() || undefined,
        isEnabled: addForm.isEnabled,
        icon: "🔗",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ]);
    setDraftIds((prev) => new Set([...prev, newId]));
    setAddForm({ name: "", url: "", description: "", isEnabled: true });
    setAddFormErrors({});
    setShowAddForm(false);
  };

  const handleStartEditCustomCap = (cap: ICustomCapability): void => {
    setEditingId(cap.id);
    setEditForm({
      name: cap.name,
      url: cap.url,
      description: cap.description ?? "",
      isEnabled: cap.isEnabled !== false,
    });
    setEditFormErrors({});
  };

  const handleSaveEditCustomCap = (capId: string): void => {
    const errors = validateCapForm(editForm);
    if (Object.keys(errors).length) {
      setEditFormErrors(errors);
      return;
    }
    setLocalCustomCaps((prev) =>
      prev.map((c) =>
        c.id === capId
          ? {
            ...c,
            name: editForm.name.trim(),
            url: editForm.url.trim(),
            description: editForm.description.trim() || undefined,
            isEnabled: editForm.isEnabled,
            updatedAt: new Date().toISOString(),
          }
          : c,
      ),
    );
    setEditingId(null);
    setEditFormErrors({});
  };

  const handleDeleteCustomCap = (capId: string): void => {
    setLocalCustomCaps((prev) => prev.filter((c) => c.id !== capId));
    setDraftIds((prev) => {
      const next = new Set(prev);
      next.delete(capId);
      return next;
    });
    if (editingId === capId) setEditingId(null);
    setConfirmingDeleteId(null);
  };

  const navigateTab = (target: WorkspaceTab): void => {
    const currentIdx = tabOrder.indexOf(activeTab);
    const targetIdx = tabOrder.indexOf(target);
    if (targetIdx > currentIdx) {
      const errors = validateDetailsFields();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
    }
    setActiveTab(target);
  };

  const handleNext = (): void => {
    const currentIdx = tabOrder.indexOf(activeTab);
    const nextTab = tabOrder[currentIdx + 1];
    if (nextTab) navigateTab(nextTab);
  };

  const handleBack = (): void => {
    const currentIdx = tabOrder.indexOf(activeTab);
    const prevTab = tabOrder[currentIdx - 1];
    if (prevTab) setActiveTab(prevTab);
  };

  const handleSubmit = async (): Promise<void> => {
    const errors: Record<string, string> = {
      ...validateDetailsFields(),
      ...validateBrandingFields(),
    };

    Object.assign(errors, validateOwners(owners));
    Object.assign(
      errors,
      validateSelfRemoval(
        currentUser.id,
        workspace?.teamMembers?.map((m) => m.userId) || [],
        teamMembers
          .map((m) => m.id)
          .filter((id): id is string => id !== undefined),
      ),
    );

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const detailsFields = ["name", "clientName"];
      const brandingFields = ["headerColor", "accentColor"];
      if (detailsFields.some((f) => errors[f])) {
        setActiveTab("details");
      } else if (brandingFields.some((f) => errors[f])) {
        setActiveTab("branding");
      }
      return;
    }

    setIsSaving(true);
    try {
      const ownerIds = new Set(owners.map((o) => o.id));
      const members: IUserDetails[] = teamMembers
        .filter((member) => member.id && !ownerIds.has(member.id))
        .map(optionToUserDetails);

      const workspacePayload: ICreateOrUpdateProjectIQWorkspace = {
        id: workspace?.id,
        name,
        clientName,
        teamMembers: members,
        owners: owners.map(optionToUserDetails),
        code,
        portfolioId: portfolioId || undefined,
        practiceIds,
        description,
        capabilities,
        customBrandingEnabled,
        ...buildBrandingColorPayloadFields(
          customBrandingEnabled,
          headerColor,
          accentColor,
        ),
        ...(workspace?.status ? { status: workspace.status } : {}),
      };

      let resultWorkspace: IProjectIQWorkspace;
      let logoSyncErrorMessage: string | undefined;

      if (workspace) {
        try {
          const logoSyncResult = await syncWorkspaceLogo(workspace.id, {
            logoFile,
            headerLogoUrl,
            originalLogoUrl,
            customBrandingEnabled,
          });
          if (customBrandingEnabled) {
            workspacePayload.headerLogoUrl = logoSyncResult.headerLogoUrl;
            setOriginalLogoUrl(logoSyncResult.headerLogoUrl ?? undefined);
            setLogoFile(null);
          }
        } catch (logoError) {
          console.warn("Failed to sync workspace logo:", logoError);
          logoSyncErrorMessage =
            "The logo failed to upload. Your previous logo has been kept — you can try again from Edit Workspace.";
        }

        const updated = onSave
          ? await onSave(workspace.id, workspacePayload)
          : await updateProjectIqWorkspace({
            id: workspace.id,
            workspace: workspacePayload,
          });
        resultWorkspace = updated ?? {
          ...workspace,
          ...workspacePayload,
          updatedAt: Math.floor(Date.now() / 1000),
        };
      } else {
        resultWorkspace = await createProjectIqWorkspace({
          workspace: workspacePayload,
        });

        try {
          const logoSyncResult = await syncWorkspaceLogo(resultWorkspace.id, {
            logoFile,
            headerLogoUrl,
            originalLogoUrl,
            customBrandingEnabled,
          });
          if (customBrandingEnabled && logoSyncResult.headerLogoUrl) {
            resultWorkspace = await updateProjectIqWorkspace({
              id: resultWorkspace.id,
              workspace: {
                ...workspacePayload,
                headerLogoUrl: logoSyncResult.headerLogoUrl,
              },
            });
          }
        } catch (logoError) {
          console.warn(
            "Failed to sync workspace logo after workspace creation:",
            logoError,
          );
          logoSyncErrorMessage =
            "Workspace created, but the logo failed to upload — you can add it later from Edit Workspace.";
        }
      }

      let finalWorkspace = resultWorkspace;
      const workspaceId = finalWorkspace.id;

      for (const orig of originalCustomCaps) {
        if (!localCustomCaps.find((c) => c.id === orig.id)) {
          finalWorkspace = await deleteCustomCapability(workspaceId, orig.id);
        }
      }
      for (const cap of localCustomCaps) {
        if (draftIds.has(cap.id)) {
          finalWorkspace = await addCustomCapability(workspaceId, {
            name: cap.name,
            description: cap.description,
            url: cap.url,
            isEnabled: cap.isEnabled,
          });
        }
      }
      for (const cap of localCustomCaps) {
        if (draftIds.has(cap.id)) continue;
        const orig = originalCustomCaps.find((o) => o.id === cap.id);
        if (
          orig &&
          (orig.name !== cap.name ||
            orig.url !== cap.url ||
            orig.description !== cap.description ||
            (orig.isEnabled !== false) !== (cap.isEnabled !== false))
        ) {
          finalWorkspace = await updateCustomCapability(workspaceId, cap.id, {
            name: cap.name,
            description: cap.description,
            url: cap.url,
            isEnabled: cap.isEnabled,
          });
        }
      }

      resultWorkspace = finalWorkspace;
      if (workspace) {
        dispatch(updateProjectIqWorkspaceAction(resultWorkspace));
      } else {
        dispatch(addProjectIqWorkspace(resultWorkspace));
      }

      const saveWarnings: string[] = [];
      if (logoSyncErrorMessage) {
        saveWarnings.push(logoSyncErrorMessage);
      }

      if (stagedFiles.length > 0) {
        const uploadFailures: string[] = [];
        for (const file of stagedFiles) {
          try {
            const fileId = crypto.randomUUID();
            const uploaded = await uploadFilesToStorage(
              [{ id: fileId, file }],
              "workspace_library",
              resultWorkspace.id,
              false,
            );
            if (uploaded.length > 0) {
              const blobFilename = safeDecodeURIComponent(
                uploaded[0].url?.split("/").pop() ||
                ConstantValues.EMPTY_STRING,
              );
              await addProjectIqWorkspaceDocument({
                workspaceId: resultWorkspace.id,
                filename: blobFilename,
                originalFilename: file.name,
                source: "file",
              });
            } else {
              console.error(
                `Upload returned no result for file "${file.name}"`,
              );
              uploadFailures.push(file.name);
            }
          } catch (uploadErr) {
            console.error(`Failed to upload file "${file.name}":`, uploadErr);
            uploadFailures.push(file.name);
          }
        }
        if (uploadFailures.length > 0) {
          saveWarnings.push(
            `${uploadFailures.length} file(s) failed to upload: ${uploadFailures.join(", ")}. You can retry from the workspace documents tab.`,
          );
        }
      }

      if (saveWarnings.length > 0) {
        setFieldErrors({
          general: `Workspace saved, but ${saveWarnings.join(" ")}`,
        });
        try {
          const freshWorkspaces = await getProjectIqWorkspaces();
          dispatch(setProjectIqWorkspaces(freshWorkspaces));
        } catch {
          setFieldErrors((prev) => ({
            ...prev,
            general:
              `${prev.general ?? ""} Additionally, failed to refresh the workspace list — please reload the page.`.trim(),
          }));
        }
        onDismiss(true, resultWorkspace);
        return;
      }

      try {
        const freshWorkspaces = await getProjectIqWorkspaces();
        dispatch(setProjectIqWorkspaces(freshWorkspaces));
      } catch {
        setFieldErrors({
          general:
            "Workspace saved, but failed to refresh the workspace list. Please reload the page to see the latest changes.",
        });
        return;
      }
      onDismiss(true, resultWorkspace);
    } catch (e) {
      console.error("Error saving Project IQ workspace:", e);
      setFieldErrors({ general: WORKSPACE_ERROR_MESSAGES.SAVE_ERROR });
    } finally {
      setIsSaving(false);
    }
  };

  const nameValidation = getFieldValidation(
    name.length,
    WORKSPACE_FIELD_LIMITS.NAME_MAX_LENGTH,
    WORKSPACE_FIELD_LIMITS.NAME_WARNING_THRESHOLD,
  );
  const clientNameValidation = getFieldValidation(
    clientName.length,
    CLIENT_NAME_MAX_LENGTH,
    CLIENT_NAME_WARNING_THRESHOLD,
  );

  const suggestedTeamName = getDefaultTeamName(code, name);
  const teamName = editedTeamName ?? suggestedTeamName;
  // TODO: Derive from the workspace once the backend exposes the connected Team.
  const isConnectedToSite = false;
  const connectedTeam: IMicrosoftTeam | null = {
    id: "team-13",
    name: "Customer Experience Strategy",
    memberCount: 29,
    ownerName: "Noah Parker",
  };
  const isTeamConnectionValid =
    isConnectedToSite ||
    (teamSource === "existing" ? selectedTeamId !== null : !!teamName.trim());

  const hasIncompleteDetails =
    !name.trim() ||
    !clientName.trim() ||
    nameValidation.state === "error" ||
    clientNameValidation.state === "error";

  return (
    <Dialog
      open={isOpen}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss(false);
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={() => onDismiss(false)}
              />
            }
          >
            {isEdit ? DIALOG_LABELS.EDIT_TITLE : DIALOG_LABELS.CREATE_TITLE}
          </DialogTitle>
          <DialogContent
            className={mergeClasses(
              contentStyles.tabbedDialogContent,
              styles.dialogContent,
            )}
          >
            {isSaving && (
              <div className={contentStyles.formLoadingOverlay}>
                <Spinner
                  label={`${isEdit ? DIALOG_LABELS.SAVING : DIALOG_LABELS.CREATING} workspace...`}
                />
              </div>
            )}
            <DialogTabs
              tabs={[
                { key: "details", label: DIALOG_LABELS.TAB_DETAILS },
                { key: "settings", label: DIALOG_LABELS.TAB_CAPABILITIES },
                //   TODO: Uncomment when the context & files tab is restored.
                // { key: "context", label: DIALOG_LABELS.TAB_CONTEXT },
                { key: "microsoftTeams", label: DIALOG_LABELS.TAB_MICROSOFT_TEAMS },
                //   TODO: Uncomment when the client view and the Custom branding tab are restored.
                // ...(customBrandingEnabled
                //   ? [{ key: "branding", label: DIALOG_LABELS.TAB_BRANDING }]
                //   : []),
              ]}
              activeTab={activeTab}
              onTabClick={(tab) => navigateTab(tab as WorkspaceTab)}
            />
            <div
              className={mergeClasses(
                contentStyles.formContainer,
                styles.scrollableContent,
              )}
            >
              {fieldErrors.general && (
                <div className={contentStyles.feedbackErrorBox} role="alert">
                  {fieldErrors.general}
                </div>
              )}

              {activeTab === "details" && (
                <div className={contentStyles.tabContent} role="tabpanel">
                  <Field
                    label={DIALOG_LABELS.WORKSPACE_NAME}
                    required
                    validationState={
                      fieldErrors.name ? "error" : nameValidation.state
                    }
                    validationMessage={
                      fieldErrors.name || nameValidation.message
                    }
                  >
                    <Input
                      value={name}
                      onChange={handleNameChange}
                      placeholder="Enter workspace name..."
                      disabled={isSaving}
                    />
                  </Field>

                  <Field
                    label={DIALOG_LABELS.CLIENT_NAME}
                    required
                    validationState={
                      fieldErrors.clientName
                        ? "error"
                        : clientNameValidation.state
                    }
                    validationMessage={
                      fieldErrors.clientName || clientNameValidation.message
                    }
                  >
                    <Input
                      value={clientName}
                      onChange={handleClientNameChange}
                      placeholder="Enter client name..."
                      disabled={isSaving}
                    />
                  </Field>

                  <Field
                    label={DIALOG_LABELS.PROJECT_CODE}
                    validationState={fieldErrors.code ? "error" : "none"}
                    validationMessage={fieldErrors.code}
                  >
                    <Input
                      value={code}
                      onChange={(_e, d) => {
                        setCode(d.value);
                        if (fieldErrors.code)
                          setFieldErrors((prev) => ({ ...prev, code: "" }));
                      }}
                      placeholder="e.g. NH-ABC-2024"
                      maxLength={50}
                      disabled={isSaving}
                    />
                  </Field>

                  <Field label={DIALOG_LABELS.PORTFOLIO}>
                    <Dropdown
                      placeholder="Select a portfolio..."
                      value={
                        portfolios.find((p) => p.id === portfolioId)?.label ??
                        ""
                      }
                      selectedOptions={portfolioId ? [portfolioId] : []}
                      onOptionSelect={(_e, data) =>
                        setPortfolioId(data.optionValue ?? "")
                      }
                      className={dropdownStyles.dropdown}
                      listbox={{ className: dropdownStyles.listbox }}
                      disabled={isSaving || isPortfoliosLoading}
                    >
                      {sortedPortfolios.map((portfolio) => (
                        <Option
                          key={portfolio.id}
                          value={portfolio.id}
                          text={portfolio.label}
                        >
                          {portfolio.label}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  <Field label={DIALOG_LABELS.PRACTICES}>
                    <Dropdown
                      multiselect
                      placeholder="Select practices..."
                      value={practiceIds
                        .map((id) => practices.find((p) => p.id === id)?.label)
                        .filter((label): label is string => Boolean(label))
                        .join(", ")}
                      selectedOptions={practiceIds}
                      onOptionSelect={(_e, data) =>
                        setPracticeIds(data.selectedOptions)
                      }
                      className={dropdownStyles.dropdown}
                      listbox={{ className: dropdownStyles.listbox }}
                      disabled={isSaving || isPracticesLoading}
                    >
                      {sortedPractices.map((practice) => (
                        <Option
                          key={practice.id}
                          value={practice.id}
                          text={practice.label}
                        >
                          {practice.label}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  <Field label={DIALOG_LABELS.DESCRIPTION}>
                    <Textarea
                      value={description}
                      onChange={(_e, d) => setDescription(d.value)}
                      resize="vertical"
                      rows={3}
                      maxLength={500}
                      disabled={isSaving}
                    />
                    <span className={styles.charCount}>
                      {description.length}/500
                    </span>
                  </Field>

                  {canEditOwners && (
                    <Field
                      label={DIALOG_LABELS.OWNERS}
                      required
                      validationState={fieldErrors.owners ? "error" : "none"}
                      validationMessage={fieldErrors.owners}
                    >
                      <PeoplePicker
                        id={WORKSPACE_PICKER_IDS.OWNERS}
                        handleSelectedItems={handleOwnersChange}
                        readResourcesForPickerFieldFiltered={
                          readResourcesForPickerFieldFiltered
                        }
                        selectedOptions={owners}
                        disabled={isSaving}
                        required={true}
                        hasOnlyOurUsers={true}
                      />
                    </Field>
                  )}

                  <Field
                    label={DIALOG_LABELS.TEAM_MEMBERS}
                    validationState={fieldErrors.teamMembers ? "error" : "none"}
                    validationMessage={fieldErrors.teamMembers}
                  >
                    <PeoplePicker
                      id={WORKSPACE_PICKER_IDS.TEAM_MEMBERS}
                      handleSelectedItems={handleSelectedTeamMembers}
                      disabled={isSaving}
                      hasOnlyOurUsers={true}
                      readResourcesForPickerFieldFiltered={
                        readResourcesForPickerFieldFiltered
                      }
                      selectedOptions={teamMembers}
                    />
                  </Field>
                  {/*   TODO: Uncomment when the client view and the Custom branding tab are restored.*/}
                  {/* <CustomToggle
                    checked={customBrandingEnabled}
                    onChange={handleCustomBrandingToggle}
                    label={DIALOG_LABELS.CUSTOM_BRANDING_LABEL}
                    hint={DIALOG_LABELS.CUSTOM_BRANDING_HINT}
                    disabled={isSaving}
                  /> */}
                </div>
              )}

              {activeTab === "settings" && (
                <div className={contentStyles.tabContent} role="tabpanel">
                  {isCapabilitiesLoadError ? (
                    <p className={contentStyles.feedbackErrorBox}>
                      Failed to load capabilities. Please close and reopen this
                      dialog.
                    </p>
                  ) : isCapabilitiesLoading ? (
                    <div className={styles.capabilityGrid}>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Skeleton
                          key={index}
                          className={styles.capabilitySkeletonCard}
                        >
                          <SkeletonItem shape="circle" size={28} />
                          <div className={styles.capabilitySkeletonMeta}>
                            <SkeletonItem size={12} />
                            <SkeletonItem size={8} />
                          </div>
                        </Skeleton>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.capabilityGrid}>
                      {availableCapabilities.map((cap) => {
                        const enabled = capabilities.includes(cap.slug);
                        return (
                          <div
                            key={cap.slug}
                            className={mergeClasses(
                              styles.capabilityCard,
                              enabled && styles.capabilityCardEnabled,
                            )}
                            onClick={() =>
                              !isSaving && toggleCapability(cap.slug)
                            }
                            role="checkbox"
                            aria-checked={enabled}
                            aria-label={cap.title}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === " " || e.key === "Enter") {
                                e.preventDefault();
                                if (!isSaving) toggleCapability(cap.slug);
                              }
                            }}
                          >
                            <div className={styles.capabilityIconWrap}>
                              <span style={{ fontSize: "20px" }}>
                                {cap.icon}
                              </span>
                            </div>
                            <div className={styles.capabilityMeta}>
                              <div className={styles.capabilityNameRow}>
                                <span className={styles.capabilityName}>
                                  {cap.title}
                                </span>
                              </div>
                              <div className={styles.capabilityDesc}>
                                {cap.shortDescription}
                              </div>
                            </div>
                            <Switch
                              className={styles.capabilityCardSwitch}
                              checked={enabled}
                              onChange={() => undefined}
                              disabled={isSaving}
                              aria-hidden="true"
                              tabIndex={-1}
                            />
                          </div>
                        );
                      })}
                      {localCustomCaps.map((cap) => (
                        <CustomCapabilityCard
                          key={cap.id}
                          cap={cap}
                          isSaving={isSaving}
                          onEditStart={() => handleStartEditCustomCap(cap)}
                          onDeleteRequest={() => setConfirmingDeleteId(cap.id)}
                        />
                      ))}
                      {editingId && (
                        <div className={styles.customCapEditCard}>
                          <div className={styles.inlineFormRow}>
                            <Field
                              label="Name"
                              required
                              validationState={
                                editFormErrors.name ? "error" : "none"
                              }
                              validationMessage={editFormErrors.name}
                            >
                              <Input
                                value={editForm.name}
                                onChange={(_e, data) => {
                                  setEditForm((prev) => ({
                                    ...prev,
                                    name: data.value,
                                  }));
                                  if (editFormErrors.name)
                                    setEditFormErrors((prev) => ({
                                      ...prev,
                                      name: "",
                                    }));
                                }}
                                placeholder="Enter capability name"
                                disabled={isSaving}
                              />
                            </Field>
                            <Tooltip
                              content={editForm.url || "No URL entered"}
                              relationship="description"
                            >
                              <Field
                                label="URL"
                                required
                                validationState={
                                  editFormErrors.url ? "error" : "none"
                                }
                                validationMessage={editFormErrors.url}
                              >
                                <Input
                                  value={editForm.url}
                                  onChange={(_e, data) => {
                                    setEditForm((prev) => ({
                                      ...prev,
                                      url: data.value,
                                    }));
                                    if (editFormErrors.url)
                                      setEditFormErrors((prev) => ({
                                        ...prev,
                                        url: "",
                                      }));
                                  }}
                                  placeholder="https://example.com"
                                  disabled={isSaving}
                                />
                              </Field>
                            </Tooltip>
                          </div>
                          <Field label="Description">
                            <Textarea
                              value={editForm.description}
                              onChange={(_e, data) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  description: data.value,
                                }))
                              }
                              placeholder="Enter optional description"
                              rows={2}
                              disabled={isSaving}
                            />
                          </Field>
                          <div className={styles.inlineFormActions}>
                            <Switch
                              label="Enabled"
                              aria-label="Enabled"
                              checked={editForm.isEnabled}
                              onChange={(_e, data) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  isEnabled: data.checked,
                                }))
                              }
                              disabled={isSaving}
                            />
                            <div className={styles.inlineFormButtons}>
                              <Button
                                appearance="outline"
                                onClick={() => {
                                  setEditingId(null);
                                  setEditForm({
                                    name: "",
                                    url: "",
                                    description: "",
                                    isEnabled: true,
                                  });
                                  setEditFormErrors({});
                                }}
                                disabled={isSaving}
                              >
                                Cancel
                              </Button>
                              <Button
                                appearance="primary"
                                onClick={() =>
                                  handleSaveEditCustomCap(editingId)
                                }
                                disabled={isSaving}
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                      {confirmingDeleteId &&
                        localCustomCaps
                          .filter((c) => c.id === confirmingDeleteId)
                          .map((capToDelete) => (
                            <div
                              key={capToDelete.id}
                              className={styles.customCapEditCard}
                            >
                              <p className={styles.inlineDeleteMessage}>
                                Remove <strong>{capToDelete.name}</strong>? This
                                action cannot be undone.
                              </p>
                              <Tooltip
                                content={capToDelete.url}
                                relationship="description"
                              >
                                <p className={styles.inlineDeleteUrl}>
                                  {capToDelete.url}
                                </p>
                              </Tooltip>
                              <div className={styles.inlineFormActions}>
                                <span />
                                <div className={styles.inlineFormButtons}>
                                  <Button
                                    appearance="outline"
                                    onClick={() => setConfirmingDeleteId(null)}
                                    disabled={isSaving}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    appearance="primary"
                                    onClick={() =>
                                      handleDeleteCustomCap(capToDelete.id)
                                    }
                                    disabled={isSaving}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                      <CustomCapabilityAddCard
                        isExpanded={showAddForm}
                        form={addForm}
                        formErrors={addFormErrors}
                        isSaving={isSaving}
                        onExpand={() => setShowAddForm(true)}
                        onChange={(field, value) => {
                          setAddForm((prev) => ({
                            ...prev,
                            [field]: value,
                          }));
                          if (addFormErrors[field as "name" | "url"])
                            setAddFormErrors((prev) => ({
                              ...prev,
                              [field]: "",
                            }));
                        }}
                        onEnabledChange={(value) =>
                          setAddForm((prev) => ({ ...prev, isEnabled: value }))
                        }
                        onSave={handleAddCustomCap}
                        onCancel={() => {
                          setShowAddForm(false);
                          setAddForm({
                            name: "",
                            url: "",
                            description: "",
                            isEnabled: true,
                          });
                          setAddFormErrors({});
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {activeTab === "context" && (
                <ContextTab
                  isSaving={isSaving}
                  connectedSources={connectedSources}
                  stagedFiles={stagedFiles}
                  onToggleSource={toggleConnectedSource}
                  onFilesSelected={handleStagedFilesSelected}
                  onRemoveFile={removeStagedFile}
                  existingDocuments={existingDocuments}
                  onRemoveExistingDocument={handleRemoveExistingDocument}
                />
              )}

              {activeTab === "microsoftTeams" && (
                <MicrosoftTeamsTab
                  isConnectedToSite={isConnectedToSite}
                  connectedTeam={connectedTeam}
                  workspaceCode={code}
                  clientName={clientName}
                  teamMembers={teamMembers}
                  teamSource={teamSource}
                  onTeamSourceChange={setTeamSource}
                  selectedTeamId={selectedTeamId}
                  onSelectedTeamIdChange={setSelectedTeamId}
                  teamName={teamName}
                  suggestedTeamName={suggestedTeamName}
                  onTeamNameChange={setEditedTeamName}
                  onTeamNameReset={() => setEditedTeamName(null)}
                />
              )}
              {/*   TODO: Remove false check when the client view and the Custom branding tab are restored.*/}
              {false && activeTab === "branding" && (
                <div
                  className={styles.brandingTabLayout}
                  role="tabpanel"
                  aria-labelledby="tab-branding"
                >
                  <div className={styles.brandingFormColumn}>
                    <Field label={DIALOG_LABELS.CLIENT_LOGO_LABEL}>
                      {headerLogoUrl ? (
                        <div className={styles.logoPreviewWrapper}>
                          <img
                            src={headerLogoUrl}
                            alt="Client logo preview"
                            className={styles.logoPreview}
                          />
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<DeleteRegular />}
                            onClick={handleLogoFileRemove}
                            aria-label="Remove logo"
                            disabled={isSaving}
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <>
                          <FileDropzone
                            onFilesSelected={handleLogoFileSelect}
                            acceptedExtensions={[
                              ".png",
                              ".jpg",
                              ".jpeg",
                              ".svg",
                            ]}
                            multiple={false}
                            disabled={isSaving}
                            onValidationError={() =>
                              setFieldErrors((prev) => ({
                                ...prev,
                                logo: "Unsupported file type. Please upload a PNG, JPG, or SVG image.",
                              }))
                            }
                          />
                          {fieldErrors.logo && (
                            <Text size={200} className={styles.errorMessage}>
                              {fieldErrors.logo}
                            </Text>
                          )}
                        </>
                      )}
                    </Field>

                    <Field
                      label={DIALOG_LABELS.HEADER_COLOR_LABEL}
                      validationState={
                        fieldErrors.headerColor ? "error" : "none"
                      }
                      validationMessage={fieldErrors.headerColor}
                    >
                      <div className={styles.colorPickerRow}>
                        <input
                          type="color"
                          value={headerColor}
                          onChange={(e) =>
                            handleHeaderColorChange(e.target.value)
                          }
                          className={styles.colorInput}
                          aria-label="Header color picker"
                        />
                        <Input
                          value={headerColor}
                          onChange={(_, d) => handleHeaderColorChange(d.value)}
                          onBlur={handleHeaderColorBlur}
                          placeholder={projectIqTokens.navy}
                          maxLength={7}
                          className={styles.hexInput}
                          disabled={isSaving}
                        />
                        <Button
                          appearance="subtle"
                          size="small"
                          onClick={() =>
                            handleHeaderColorChange(projectIqTokens.navy)
                          }
                          disabled={
                            headerColor.toLowerCase() ===
                            projectIqTokens.navy.toLowerCase() || isSaving
                          }
                        >
                          Reset to default
                        </Button>
                      </div>
                    </Field>

                    <Field
                      label={DIALOG_LABELS.ACCENT_COLOR_LABEL}
                      validationState={
                        fieldErrors.accentColor ? "error" : "none"
                      }
                      validationMessage={fieldErrors.accentColor}
                    >
                      <div className={styles.colorPickerRow}>
                        <input
                          type="color"
                          value={accentColor}
                          onChange={(e) =>
                            handleAccentColorChange(e.target.value)
                          }
                          className={styles.colorInput}
                          aria-label="Accent color picker"
                        />
                        <Input
                          value={accentColor}
                          onChange={(_, d) => handleAccentColorChange(d.value)}
                          onBlur={handleAccentColorBlur}
                          placeholder={projectIqTokens.teal}
                          maxLength={7}
                          className={styles.hexInput}
                          disabled={isSaving}
                        />
                        <Button
                          appearance="subtle"
                          size="small"
                          onClick={() =>
                            handleAccentColorChange(projectIqTokens.teal)
                          }
                          disabled={
                            accentColor.toLowerCase() ===
                            projectIqTokens.teal.toLowerCase() || isSaving
                          }
                        >
                          Reset to default
                        </Button>
                      </div>
                    </Field>
                  </div>
                  <div className={styles.brandingPreviewColumn}>
                    <Caption1 as="span" className={styles.previewLabel}>
                      Preview
                    </Caption1>
                    {!isSaving && (
                      <WorkspaceBrandingPreview
                        headerColor={headerColor}
                        accentColor={accentColor}
                        headerLogoUrl={headerLogoUrl || undefined}
                        workspaceName={name || undefined}
                        clientName={clientName.trim() || undefined}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </DialogBody>
        {!isSaving && (
          <div className={footerStyles.dialogFooter}>
            {activeTab !== "details" && (
              <DialogFooterAction
                icon={<ArrowLeftRegular />}
                tooltip="Back"
                onClick={handleBack}
              />
            )}
            {activeTab !== lastTab && (
              <DialogFooterAction
                icon={<ArrowRightRegular />}
                tooltip="Next"
                onClick={handleNext}
              />
            )}
            {activeTab === lastTab && (
              <DialogFooterAction
                icon={<CheckmarkRegular />}
                tooltip="Save"
                dimmed={hasIncompleteDetails || !isTeamConnectionValid}
                disabled={!isTeamConnectionValid}
                ariaLabel={
                  hasIncompleteDetails || !isTeamConnectionValid
                    ? "Save (complete required fields first)"
                    : "Save"
                }
                onClick={handleSubmit}
              />
            )}
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
